import { Alert, FormErrorMessage, TextInput } from '@ksyos/design-system';
import { Map } from 'immutable';
import { useEffect, useState } from 'react';
import type { ICheckedTreatment } from '@common/interfaces/checkedTreatment';
import { isEmailDuplicateForPatient } from '@frontend/services/api/therapielandApiService';
import { scrollToValidationErrorByClassName } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { getValidationErrorTranslation } from '../../../../../utils/getValidationErrorTranslation';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { showServiceToast } from '../../../../../utils/showToast';
import { emptyValidationErrors } from '../../../../../utils/validation';
import { CheckMarkButton } from '../../../../elements/buttons/checkMarkButton/CheckMarkButton';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import {
    ButtonContainer,
    Form,
    FormDivider,
    FormParagraph,
    HeaderDivider,
    IconOnClick,
} from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { TextAreaInput } from '../../../../fields/TextArea';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { CounselorSelector } from '../../utils/v1/CounselorSelector';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { GgzbTherV1FollowingSteps } from './GgzbTherV1FollowingStepts';
import { GgzbTherV1ProgrammeLinks } from './GgzbTherV1ProgrammeLinks';
import { GgzbTherV1TreatmentListModal } from './GgzbTherV1TreatmentListModal';

export interface ICustomControlDataGgzbTher {
    checkedTreatments: ICheckedTreatment[];
    emailadres: string | null;
    identifierSocialSecurity: string | null;
    nameGiven: string | null;
}

export function GgzbTherV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const abortSignal = useAbortSignal();
    const { patient } = useEpisodeContext();
    const [bsnKnown, setBsnKnown] = useState(false);
    const [nameGivenKnown, setNameGivenKnown] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [showTreatmentListModal, setShowTreatmentListModal] = useState(false);
    const [showEmailAddressError, setShowEmailAddressError] = useState(false);

    useEffect(() => {
        const update: { identifierSocialSecurity?: string; nameGiven?: string } = {};
        if (props.request.customControlData.identifierSocialSecurity) {
            setBsnKnown(true);
        } else if (patient.identifierSocialSecurity) {
            update.identifierSocialSecurity = patient.identifierSocialSecurity;
            setBsnKnown(true);
        }
        if (props.request.customControlData.nameGiven) {
            setNameGivenKnown(true);
        } else if (patient.nameGiven) {
            update.nameGiven = patient.nameGiven;
            setNameGivenKnown(true);
        }

        props.handleChangeCustomControl(update);
    }, []);

    useEffect(() => {
        if (patient.emailAddress && !props.request.customControlData.emailadres) {
            updateEmailAddressFromPatient(patient.emailAddress);
        }
    }, [patient.emailAddress]);

    useEffect(() => {
        checkDuplicateTreatments(props.request.customControlData.checkedTreatments);
    }, [props.request.customControlData.checkedTreatments]);

    return (
        <NewOrEditRequestProcessContainer
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            process={props.process}
            service={props.service}
            title="Therapieland programma's"
        >
            {addEditForm()}
        </NewOrEditRequestProcessContainer>
    );

    function addEditForm() {
        const bsnHasErrors =
            errors.has('customControlData.identifierSocialSecurity') ||
            props.errors.has('customControlData.identifierSocialSecurity');

        return (
            <>
                <GgzbTherV1ProgrammeLinks />
                <Form onSubmit={async () => null}>
                    <HorizontalFormGroup>
                        <FormLabel>Programma's</FormLabel>
                        <InputContainer>
                            {props.request.customControlData.checkedTreatments
                                .sort((a: ICheckedTreatment, b: ICheckedTreatment) =>
                                    a.treatmentName.localeCompare(b.treatmentName)
                                )
                                .map((checkedTreatment: ICheckedTreatment) => (
                                    <FormParagraph
                                        bold
                                        key={checkedTreatment.id}
                                    >
                                        {`${checkedTreatment.treatmentName} `}
                                        <IconOnClick
                                            icon={'delete'}
                                            onClick={async () => await deleteCheckedTreatment(checkedTreatment.id)}
                                        />
                                    </FormParagraph>
                                ))}

                            <ButtonContainer
                                buttons={[
                                    <CheckMarkButton
                                        isChecked={false}
                                        onClick={() => setShowTreatmentListModal(true)}
                                    >
                                        Selecteer programma's
                                    </CheckMarkButton>,
                                ]}
                                className={props.errors.has('customControlData.checkedTreatments') ? 'has-danger' : ''}
                                noMargin
                                wrapContent
                            />
                            {props.errors.has('customControlData.checkedTreatments') && (
                                <FormErrorMessage>{getValidationErrorTranslation('selectTreatment')}</FormErrorMessage>
                            )}
                        </InputContainer>
                    </HorizontalFormGroup>

                    <GgzbTherV1TreatmentListModal
                        checkDuplicates={async (treatments: ICheckedTreatment[]) =>
                            await checkDuplicateTreatments(treatments)
                        }
                        checkedTreatments={props.request.customControlData.checkedTreatments}
                        errors={errors}
                        onCloseModal={() => setShowTreatmentListModal(false)}
                        onSave={async (checkedTreatments: ICheckedTreatment[]) => {
                            setShowTreatmentListModal(false);
                            await props.handleChangeCustomControl({ checkedTreatments });
                            if (props.errors.has('customControlData.checkedTreatments')) {
                                await props.validateOneCustomControl('checkedTreatments');
                            }
                        }}
                        organizationId={props.organizationId}
                        selectedPractitionerId={props.request.selectedDiagnosticReportConsumerPractitionerId || null}
                        showModal={showTreatmentListModal}
                    />

                    <CounselorSelector
                        {...props}
                        careProviderAndRequesterAndSelectedConsumers={{
                            ...props.request,
                            requesterOrganizationId: props.organizationId,
                            selectedDiagnosticReportConsumerPractitionerId:
                                props.request.customControlData.selectedDiagnosticReportConsumerPractitionerId,
                        }}
                        procedurePerformerOrganizationId={props.organizationId}
                    />

                    <FormDivider />

                    <HeaderDivider text={translateKey('patient.detailsCounselling')} />

                    <Alert appearance="warning">Controleer goed. Klopt het e-mailadres van de patiënt?</Alert>

                    {(!nameGivenKnown || props.errors.has('customControlData.nameGiven')) && (
                        <HorizontalFormGroup hasErrors={props.errors.has('customControlData.nameGiven')}>
                            <FormLabel isRequired>{translateKey('common.nameGiven')}</FormLabel>
                            <InputContainer>
                                <TextAreaInput
                                    error={props.errors.get('customControlData.nameGiven')}
                                    onChange={async (value) =>
                                        await props.handleChangeCustomControl({ nameGiven: value })
                                    }
                                    onDoValidate={async () => await props.validateOneCustomControl('nameGiven')}
                                    placeholder="Voer de voornaam in"
                                    value={props.request.customControlData.nameGiven}
                                />
                            </InputContainer>
                        </HorizontalFormGroup>
                    )}

                    {errors.get('customControlData.identifierSocialSecurity') === 'duplicate' && (
                        <Alert appearance="warning">{translateKey('patient.duplicateSocialSecurity')}</Alert>
                    )}

                    {(!bsnKnown || bsnHasErrors) && (
                        <HorizontalFormGroup hasErrors={bsnHasErrors}>
                            <FormLabel isRequired>{translateKey('patient.identifierSocialSecurity')}</FormLabel>
                            <InputContainer>
                                <TextAreaInput
                                    error={props.errors.get('customControlData.identifierSocialSecurity')}
                                    onChange={async (value) =>
                                        await props.handleChangeCustomControl({ identifierSocialSecurity: value })
                                    }
                                    onDoValidate={async () =>
                                        await props.validateOneCustomControl('identifierSocialSecurity')
                                    }
                                    placeholder="Voer het BSN in"
                                    value={props.request.customControlData.identifierSocialSecurity}
                                />
                            </InputContainer>
                        </HorizontalFormGroup>
                    )}

                    <HorizontalFormGroup
                        hasErrors={
                            !!getEmailAddressError(
                                showEmailAddressError,
                                props.errors.get('customControlData.emailadres') ||
                                    errors.get('customControlData.emailadres')
                            )
                        }
                    >
                        <FormLabel isRequired>E-mailadres</FormLabel>
                        <InputContainer>
                            <TextInput
                                error={getEmailAddressError(
                                    showEmailAddressError,
                                    props.errors.get('customControlData.emailadres') ||
                                        errors.get('customControlData.emailadres')
                                )}
                                onBlur={async () => {
                                    await props.validateOneCustomControl('emailadres');
                                    setShowEmailAddressError(true);
                                }}
                                onChange={async ({ target: { value } }) => {
                                    setShowEmailAddressError(false);
                                    await props.handleChangeCustomControl({ emailadres: value });
                                }}
                                placeholder="E-mailadres van de patiënt voor het versturen van de uitnodiging..."
                                value={props.request.customControlData.emailadres || ''}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <CareProviderSelector
                        {...props}
                        entity={props.request}
                    />

                    <GgzbTherV1FollowingSteps />

                    <ButtonContainer
                        alignRight
                        buttons={[
                            <CancelButton
                                cancel={props.onCancel}
                                entity={ProcessEntity.request}
                                isExisting={!!props.request.id}
                                serviceCode={props.service.code}
                                serviceType={props.service.serviceType}
                            />,
                            <SaveButton
                                buttonDisabled={buttonDisabled}
                                save={async () => {
                                    await saveRequest('draft');
                                }}
                            />,
                            <SaveSendButton
                                buttonDisabled={buttonDisabled}
                                saveSend={async () => {
                                    await saveRequest('final');
                                }}
                            />,
                        ]}
                    />
                </Form>
            </>
        );
    }

    async function deleteCheckedTreatment(checkedTreatmentIdToDelete: number) {
        await props.handleChangeCustomControl({
            checkedTreatments: [...props.request.customControlData.checkedTreatments].filter(
                (treatment) => checkedTreatmentIdToDelete !== treatment.id
            ),
        });
    }

    async function saveRequest(saveType: 'draft' | 'final') {
        setButtonDisabled(true);
        await props.validateOneCustomControl('emailadres');
        setShowEmailAddressError(true);

        const validatedRequest = await props.validateRequest(saveType);
        if (!validatedRequest) {
            setButtonDisabled(false);

            return;
        }

        setErrors(emptyValidationErrors);
        const data = {
            id: patient.id,
            identifierSocialSecurity: props.request.customControlData.identifierSocialSecurity,
        };
        const result = await fetchJson('/api/patients/duplicates', 'POST', data);
        if (result.json.count > 0) {
            setErrors(Map({ 'customControlData.identifierSocialSecurity': 'duplicate' }));
            setButtonDisabled(false);
            scrollToValidationErrorByClassName('form-container');

            return;
        }
        const duplicateTreatments = await checkDuplicateTreatments(props.request.customControlData.checkedTreatments);
        if (duplicateTreatments) {
            setButtonDisabled(false);

            return;
        }

        const duplicateEmail = await isEmailDuplicateForPatient(
            props.request.customControlData.emailadres,
            props.request.patientId,
            abortSignal
        );

        if (duplicateEmail) {
            setErrors(Map({ 'customControlData.emailadres': 'duplicateEmailTherapieland' }));
            setButtonDisabled(false);

            return;
        }

        const requestId = await props.onSaveRequest(saveType);

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                undefined,
                props.service,
                'request',
                `Het programma is verstuurd naar ${getDisplayName(patient)}`
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }
        if (props.reloadPatient) {
            await props.reloadPatient();
        }
        await props.onSave(requestId);
    }

    async function checkDuplicateTreatments(checkedTreatments: ICheckedTreatment[]) {
        setErrors(emptyValidationErrors);
        const checkedTreatmentsData = {
            checkedTreatments,
            patientId: props.request.patientId,
        };
        if (checkedTreatments.length > 0) {
            const duplicateTreatments = await fetchJson(
                '/api/therapieland/checkDuplicateTreatments',
                'POST',
                checkedTreatmentsData
            );
            if (duplicateTreatments.json.isDuplicate) {
                const duplicateTreatmentErrors: { [key: string]: string } = {};
                for (const treatment of duplicateTreatments.json.listOfDuplicateTreatments) {
                    const errorTreatments = `customControlData.checkedTreatments.${treatment.id}`;
                    duplicateTreatmentErrors[errorTreatments] = 'duplicateTreatment';
                }
                setErrors(Map(duplicateTreatmentErrors));

                return true;
            }
        }
    }

    async function updateEmailAddressFromPatient(emailAddress: string) {
        await props.handleChangeCustomControl({ emailadres: emailAddress });
        await props.validateOneCustomControl('emailadres');

        if (emailAddress) {
            setShowEmailAddressError(true);
        }
    }
}

export function ggzbTherV1RequestInitializeCustomControlData(): ICustomControlDataGgzbTher {
    return {
        emailadres: null,
        checkedTreatments: [],
        identifierSocialSecurity: null,
        nameGiven: null,
    };
}

function getEmailAddressError(showError: boolean, error: string | undefined): string | undefined {
    return showError ? getValidationErrorTranslation(error) : undefined;
}
